// Birth-weight / birth-length SDS against the Dutch SGA reference
// (NL5def, growthscreener::calculate_birth_z). Indexed by sex + completed
// gestational age weeks (25..42). Used to label a baby AGA/SGA/LGA on the
// first measurement card.
//
// Wgt M-column is kg; we accept grams and divide internally to match
// growthscreener's API.
import { valueToSDS, type LMS, type Sex } from './lms.ts';

type GaTable = Record<number, LMS>;

// L is constant per sex per metric — Cole 1988 fitted these from birth data.
// Source: growthscreener/data-raw/R/create_ref.nl5defSGAgewicht.R + lengte.R.
const WEIGHT_L = { M: 1.0885, F: 0.8089 } as const;
const WEIGHT_TABLES: Record<Sex, GaTable> = { M: {}, F: {} };
// Interleaved male, female × ga 25..42 → 36 rows. (M_kg, S) pairs.
const WGT_M_FLAT = [
  0.962, 0.877, 0.985, 0.901, 1.042, 0.953, 1.131, 1.034, 1.249, 1.144,
  1.395, 1.285, 1.565, 1.452, 1.76, 1.641, 1.98, 1.851, 2.222, 2.08,
  2.479, 2.324, 2.745, 2.574, 3.01, 2.823, 3.268, 3.062, 3.513, 3.284,
  3.74, 3.484, 3.947, 3.66, 3.947, 3.66,
];
const WGT_S_FLAT = [
  0.2111, 0.1805, 0.2061, 0.194, 0.2031, 0.2035, 0.2014, 0.2086, 0.2003, 0.2089,
  0.1984, 0.2051, 0.1952, 0.1984, 0.1912, 0.19, 0.1867, 0.1808, 0.1818, 0.1718,
  0.1762, 0.1636, 0.1702, 0.1566, 0.1646, 0.1511, 0.1602, 0.147, 0.1577, 0.1441,
  0.1575, 0.1425, 0.1599, 0.1418, 0.1599, 0.1418,
];

const LENGTH_L = 1; // both sexes
const LENGTH_TABLES: Record<Sex, GaTable> = { M: {}, F: {} };
const LEN_M_FLAT = [
  28.49, 28.6, 30.68, 30.66, 32.8, 32.66, 34.85, 34.58, 36.82, 36.44,
  38.71, 38.22, 40.52, 39.93, 42.26, 41.57, 43.91, 43.12, 45.47, 44.59,
  46.94, 45.97, 48.32, 47.27, 49.6, 48.47, 50.79, 49.59, 51.88, 50.62,
  52.87, 51.56, 53.76, 52.42, 53.76, 52.42,
];
const LEN_S_FLAT = [
  0.1261, 0.0933, 0.1134, 0.0955, 0.1025, 0.0951, 0.0931, 0.0924, 0.0846, 0.0874,
  0.0769, 0.0811, 0.0699, 0.074, 0.0638, 0.067, 0.0586, 0.0605, 0.0541, 0.0549,
  0.0502, 0.0504, 0.0468, 0.047, 0.0438, 0.0447, 0.0413, 0.0432, 0.0395, 0.0424,
  0.0382, 0.0422, 0.0377, 0.0425, 0.0377, 0.0425,
];

for (let i = 0; i < 18; i++) {
  const ga = 25 + i;
  WEIGHT_TABLES.M[ga] = { L: WEIGHT_L.M, M: WGT_M_FLAT[i * 2], S: WGT_S_FLAT[i * 2] };
  WEIGHT_TABLES.F[ga] = { L: WEIGHT_L.F, M: WGT_M_FLAT[i * 2 + 1], S: WGT_S_FLAT[i * 2 + 1] };
  LENGTH_TABLES.M[ga] = { L: LENGTH_L, M: LEN_M_FLAT[i * 2], S: LEN_S_FLAT[i * 2] };
  LENGTH_TABLES.F[ga] = { L: LENGTH_L, M: LEN_M_FLAT[i * 2 + 1], S: LEN_S_FLAT[i * 2 + 1] };
}

export type BirthZInput = {
  sex: Sex | null | undefined;
  /** Completed gestational age weeks (25..42). Clamped to the table range. */
  gaWeeks: number | null | undefined;
};

/** Birth-weight SDS. Grams in, z out. Returns null when the inputs don't
 *  uniquely select a table row (missing sex or ga out of [25,42]). */
export function birthWeightZ(grams: number, { sex, gaWeeks }: BirthZInput): number | null {
  if (sex !== 'M' && sex !== 'F') return null;
  if (gaWeeks == null) return null;
  const ga = Math.max(25, Math.min(42, Math.round(gaWeeks)));
  const lms = WEIGHT_TABLES[sex][ga];
  if (!lms) return null;
  return valueToSDS(grams / 1000, lms);
}

/** Birth-length SDS. cm in, z out. */
export function birthLengthZ(cm: number, { sex, gaWeeks }: BirthZInput): number | null {
  if (sex !== 'M' && sex !== 'F') return null;
  if (gaWeeks == null) return null;
  const ga = Math.max(25, Math.min(42, Math.round(gaWeeks)));
  const lms = LENGTH_TABLES[sex][ga];
  if (!lms) return null;
  return valueToSDS(cm, lms);
}

export type BirthClassification = 'SGA' | 'AGA' | 'LGA';

/** Standard JGZ classification: z<-2 SGA, z>2 LGA, otherwise AGA. */
export function classifyBirthZ(z: number): BirthClassification {
  if (z < -2) return 'SGA';
  if (z > 2) return 'LGA';
  return 'AGA';
}
